import React from "react";
import { Link } from "react-router-dom";

// ⬇️ Put your PNGs in the same folder (or adjust paths)
import evaluatorImg from "./images/evaluator.svg";
import decisionImg from "./images/decision.svg";
import explanationImg from "./images/explanation.svg";

export default function Home() {
  return (
    <div className="landing-root">
      <style>{`
        :root {
          --bg0: #0A0B0E;
          --bg1: #0B0D12;
          --ink: #EAF2FF;
          --muted: #9AA4B2;
          --stroke: #1E2330;
          --accent: #46C8FF;
        }
        .landing-root {
          min-height: 100vh;
          background: linear-gradient(180deg, var(--bg0), var(--bg1));
          color: var(--ink);
          font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, system-ui, sans-serif;
        }
        .wrap { max-width: 1120px; margin: 0 auto; padding: 96px 20px 64px; text-align: center; }
        .eyebrow {
          display: inline-flex; gap: 10px; align-items: center; color: #bfeaff;
          letter-spacing: .6px; font-size: 12px; text-transform: uppercase; opacity: .9;
        }
        .eyebrow::before { content: ""; width: 42px; height: 2px; border-radius: 1px;
          background: linear-gradient(90deg, transparent, var(--accent)); display: inline-block; }
        h1.title {
          margin: 10px 0 8px; font-size: clamp(36px, 6vw, 56px); line-height: 1.04; letter-spacing: -0.4px;
          background: linear-gradient(135deg, #f2f7ff 30%, #cfe9ff 60%, var(--accent) 100%);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
        }
        p.sub { max-width: 800px; margin: 0 auto; color: var(--muted); font-size: 18px; line-height: 1.55; }
        .cta-row { margin-top: 28px; display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; }
        .btn { display: inline-flex; align-items: center; gap: 10px; padding: 12px 18px; border-radius: 12px;
          text-decoration: none; font-weight: 800; font-size: 14px; letter-spacing: .2px; border: none; }
        .btn-primary { background: linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%); color: #fff; }
        .btn-secondary { background: transparent; color: #cfe9ff; border: 1px solid #2a3546; }

        .hero-art {
          margin-top: 36px; border: 1px solid var(--stroke); border-radius: 18px; padding: 20px;
          background: linear-gradient(135deg, #0F1220 0%, #0C0F18 100%);
        }
        .art-grid { display: grid; grid-template-columns: 1fr; gap: 16px; }
        .art-row { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; }
        .chip {
          font-size: 12px; letter-spacing: .2px; border: 1px solid #243243; border-radius: 999px;
          padding: 8px 12px; background: rgba(70, 200, 255, 0.06); color: #bfeaff;
        }

        .mock { display: grid; gap: 10px; grid-template-columns: repeat(3, minmax(220px, 1fr)); margin-top: 12px; }
        .shot {
          aspect-ratio: 16/10; border: 1px solid #243243; border-radius: 12px; overflow: hidden;
          background: #0c0f17; display: grid; place-items: center;
        }
        .shot img {
          width: 100%; height: 100%; object-fit: cover; display: block;
          filter: saturate(1.05) contrast(1.05);
        }
        .caption { margin-top: 6px; color: #a8c7df; font-size: 12px; text-align: center; opacity: .85; }

        .strip {
          margin-top: 48px; padding: 18px; border: 1px solid var(--stroke); border-radius: 16px;
          background: linear-gradient(180deg, rgba(70, 200, 255, .08), transparent 60%);
        }
        .strip h3 { margin: 0; font-size: 18px; letter-spacing: .2px; }
        .strip p { margin: 6px 0 0; color: var(--muted); font-size: 14px; }

        @media (max-width: 860px) {
          .wrap { padding-top: 72px; }
          .mock { grid-template-columns: 1fr; }
        }
      `}</style>

      <div className="wrap">
        {/* HERO */}
        <section className="hero">
          <div className="eyebrow">Agentic AI for Trading</div>
          <h1 className="title">The trading AI that learns.</h1>
          <p className="sub">
            Berbul.ai evaluates every signal, executes with discipline, and adapts with experience.
            It learns from each trade through short- and long-term memory to become smarter, safer,
            and more explainable over time.
          </p>

          <div className="cta-row">
            <Link className="btn btn-primary" to="/Login">Connect Your Broker</Link>
            <Link className="btn btn-secondary" to="/Live-New">Live Trading</Link>
          </div>

          {/* Screenshot row */}
          <div className="hero-art" aria-label="concept visuals">
            <div className="art-grid">
              <div className="art-row">
                <span className="chip">Evaluates before execution</span>
                <span className="chip">Learns from results</span>
                <span className="chip">Explains every trade</span>
              </div>
              <div className="mock">
                <div>
                  <div className="shot">
                    <img
                      src={evaluatorImg}
                      alt="Evaluator Snapshot — AI decides trade or skip"
                      loading="lazy"
                    />
                  </div>
                  <div className="caption">AI decides whether to trade or skip</div>
                </div>

                <div>
                  <div className="shot">
                    <img
                      src={decisionImg}
                      alt="Decision Timeline — entry, adjust, exit with reasons"
                      loading="lazy"
                    />
                  </div>
                  <div className="caption">Entry → Adjust → Exit with reasoning</div>
                </div>

                <div>
                  <div className="shot">
                    <img
                      src={explanationImg}
                      alt="Explanation Panel — human-readable audit of trade"
                      loading="lazy"
                    />
                  </div>
                  <div className="caption">Human-readable audit of every trade</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="strip">
          <h3>Purpose-built for traders</h3>
          <p>
            Unlike general AI platforms, Berbul.ai is designed specifically for financial trading —
            to think before it acts, monitor while in position, and explain every move clearly.
          </p>
        </section>
      </div>
    </div>
  );
}
